import { useQuery } from 'react-query';
import { useToastProvider } from '@qualio/ui-components';
import { UserTaskDetails } from '../types';
import userTasksApi from '../api/userTasks.api';
import { DefaultErrorMessage } from '../displayStrings';
import { isNotFoundError } from '../utils/errorUtils';

type UseTaskDetailsType = {
  task: UserTaskDetails | undefined;
  isTaskLoading: boolean;
  isTaskError: boolean;
};

export const useTaskDetails = (
  companyId: number,
  taskId: number,
  enabled = true,
  onSuccess?: (data: UserTaskDetails) => void,
): UseTaskDetailsType => {
  const { showToast } = useToastProvider();

  const {
    data: task,
    isLoading: isTaskLoading,
    isError: isTaskError,
  } = useQuery(['taskDetails', taskId], () => userTasksApi.getUserTaskDetails(companyId, taskId), {
    // A missing task is a terminal outcome: don't retry it (react-query defaults to 3),
    // and let the view render the not-found page instead of firing a generic error toast.
    retry: (failureCount, error) => !isNotFoundError(error) && failureCount < 3,
    onError: (error) => {
      if (isNotFoundError(error)) {
        return;
      }
      showToast({
        description: DefaultErrorMessage,
        status: 'error',
      });
    },
    onSuccess: (data: UserTaskDetails) => {
      if (onSuccess) {
        onSuccess(data);
      }
    },
    enabled: enabled && !!taskId,
  });

  return { task, isTaskLoading, isTaskError };
};
